import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { usePlatform } from '../store';
import { pageMetadata, safeImageUrl, type PageMetadata as Metadata } from '../../shared/seo';

export function applyPageMetadata(metadata: Metadata) {
  const origin =
    document.querySelector<HTMLMetaElement>('meta[name="moyu:site-origin"]')?.content ||
    location.origin;
  document.title = metadata.title;
  const meta = (attribute: 'name' | 'property', key: string, value: string | null) => {
    let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
    if (value === null) {
      element?.remove();
      return;
    }
    if (!element) {
      element = document.createElement('meta');
      element.setAttribute(attribute, key);
      document.head.append(element);
    }
    element.content = value;
  };
  meta('name', 'description', metadata.description);
  meta('name', 'robots', metadata.index ? 'index, follow' : 'noindex, follow');
  meta('property', 'og:title', metadata.title);
  meta('property', 'og:description', metadata.description);
  meta('property', 'og:image', safeImageUrl(metadata.image, origin));
  meta('property', 'og:locale', 'zh_CN');
  meta('name', 'twitter:card', 'summary_large_image');
  const url = metadata.canonicalPath ? new URL(metadata.canonicalPath, origin).href : null;
  meta('property', 'og:url', url);
  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!url) canonical?.remove();
  else {
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.append(canonical);
    }
    canonical.href = url;
  }
}

export function PageMetadata() {
  const { pathname, search } = useLocation();
  const { data } = usePlatform();
  useEffect(() => {
    const path = pathname.replace(/\/+$/, '') || '/';
    // Detail owns its result/error metadata, including authenticated previews.
    if (/^\/game\/[^/]+$/.test(path)) return;
    if (path.startsWith('/play/')) {
      const id = path.slice(6),
        game = data?.games.find((g) => g.id === id || g.slug === id);
      const metadata = pageMetadata(path);
      if (game) metadata.title = game.name + ' — 正在游玩｜摸鱼岛';
      applyPageMetadata(metadata);
    } else applyPageMetadata(pageMetadata(path, undefined, search));
  }, [pathname, search, data?.games]);
  return null;
}
